import { useEffect, useState } from 'react';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { operationsSearch, productionOrdersSearch } from '@reducers/search';
import {
  GlobalSearchItem,
  OperationSearchItem,
  ProductionOrderSearchItem,
} from '../../../../interfaces/search';

import { useManageOperationsBlock } from './useManageOperationsBlock';

export interface UseManageProductionOrderBlockParams {
  item: GlobalSearchItem | null;
  existingProductionOrder?: ProductionOrderSearchItem;
  existingPart?: GlobalSearchItem | null;
}

export const useManageProductionOrderBlock = ({
  item,
  existingProductionOrder,
  existingPart,
}: UseManageProductionOrderBlockParams) => {
  const dispatch = useAppDispatch();

  const [productionOrder, setProductionOrder] = useState<ProductionOrderSearchItem | undefined>(existingProductionOrder);

  const base = useManageOperationsBlock<OperationSearchItem>({
    existingPart,
    partId: productionOrder?.partId,
  });
  const { setOperations, setIsError, setIsLoading } = base;

  useEffect(() => {
    if (existingProductionOrder) {
      return;
    }

    (async () => {
      setIsLoading(true);
      const resProductionOrders = await dispatch(
        productionOrdersSearch({
          data: { search: '', top: 100, skip: 0 },
          filter: { oDataFilter: `ProductionOrderId eq ${item?.itemId}` },
          resetHistory: true,
          ignoreHistory: true,
        })
      );

      if (resProductionOrders?.searchResult) {
        setProductionOrder(resProductionOrders.searchResult[0]);
      } else {
        setIsError(true);
      }
      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!productionOrder) {
      return;
    }

    (async () => {
      setIsLoading(true);

      try {
        const res = await dispatch(
          operationsSearch({
            data: { search: '', top: 100, skip: 0 },
            filter: {
              oDataFilter: `ProductionOrderId eq ${productionOrder?.productionOrderId}`,
              orderBy: ['SortOrder asc'],
            },
            select: null,
            resetHistory: true,
            ignoreHistory: true,
          })
        );

        if (res?.searchResult) {
          setOperations(res.searchResult);
        } else {
          setIsError(true);
        }
      } catch {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [productionOrder]);

  return {
    ...base,
    productionOrder,
    setProductionOrder,
  };
};
